import * as React from 'react';
import { TableComponent, ColumnInfo } from 'components/common/tableComponent';
import strings from 'utils/strings';
import { killEvent } from 'utils/domUtils';
import PXLDropdown, { PXLDropdownType } from 'components/PXL/pxlDropdown';
import { getName, getRoleNames } from 'utils/user';
import './userTable.scss';

// Data structure for actions to display in each row of user table
export interface UserAction {
    icon?: string;                   // icon to display in link
    text?: string;                   // text to display in link
    tooltip?: string;                // tooltip to display on link hover
    callback?: (user: User) => void;  // callback to execute upon click on link
}

export const sortUserDetails = (item1: User, item2: User): number => {
    // sort column based on display name, not email address
    return item1.displayName.localeCompare(item2.displayName);
};

export const sortUserRole = (item1: User, item2: User): number => {
    // sort list of roles alphabetically
    const item1Roles = item1.roles.map(r => r.name).sort().join(' ') || '';
    const item2Roles = item2.roles.map(r => r.name).sort().join(' ') || '';
    return item1Roles.localeCompare(item2Roles);
};

export const sortLastLogin =  (item1: User, item2: User): number => {
    // sort by last login date, with any unknown/undefined at the bottom
    if (item1.lastLogin && item2.lastLogin) {
        // comparing ms since epoch, larger number = more recent
        return item2.lastLogin.getTime() - item1.lastLogin.getTime();
    } else if (item1.lastLogin) {
        return -1;
    } else if (item2.lastLogin) {
        return 1;
    } else {
        return 0;
    }
};

interface MouseEventHandler {
    (e: React.SyntheticEvent<HTMLAnchorElement>): void;
}

// Render basic user details: first and last name and email
const renderUserDetails = (user: User) => {
    return (
        <div className="userinfo">
            <div className="userinfo__name">
                {getName(user)}
            </div>
            <div className="userinfo__email">
                {user.userName}
            </div>
        </div>
    );
};

const renderUserRole = (user: User): React.ReactNode => {
    return user.roles && (
        <div className="role">
            {getRoleNames(user)}
        </div>
    );
};

const renderLastLogin = (user: User) => {

    // TODO this is always going to be empty for now.
    return (
        <div className="lastlogin">
            {renderDateText(user)}
        </div>
    );
};

const renderDateText = (user: User): string => {
    if (!user.lastLogin) { return strings.lastLoginUnknown; }

    const prefix = user.isPending ? 'Invite Created: ' : '';

    return `${prefix}${new Date(user.lastLogin).toLocaleString()}`;
};

const renderLockedState = (user: User, unlockUserCallback?: (user: User) => void) => {
    if (!user.locked) { return null; }
    const children = [] as JSX.Element[];
    let dropdown: PXLDropdownType;

    const callbackAndCloseDropdown = () => {
        unlockUserCallback?.(user);
        dropdown.toggle();
    };

    children.push(
        (
            <button
                key={''}
                className="pxl-dropdown__item"
                onClick={callbackAndCloseDropdown}
            >
                {strings.unlockUser}
            </button>
        )
    );
    return (
        <PXLDropdown
            btnText={strings.locked}
            btnClass="pxl-pill pxl-pill-secondary user-table__locked-pill"
            ref={(ref: PXLDropdownType) => dropdown = ref}
        >
            {children}
        </PXLDropdown>
    );
};

const renderUserActions = (userActions: UserAction[], inviteActions: UserAction[], user: User) => {
    const buildClickHandler = (callback: (user: User) => void): MouseEventHandler => {
        return (e: React.SyntheticEvent<HTMLElement>) => {
            killEvent(e);
            if (callback) {
                callback(user);
            }
        };
    };

    const actions = user.isPending ? inviteActions : userActions;

    return (
        <div className="user-actions">
            {
                actions
                .map((action, idx) => {
                    const callback = action.callback ?? (() => {});
                    return (
                        <a
                            className="user-action"
                            key={`${action.text}_${idx}`}
                            href="#"
                            onClick={buildClickHandler(callback)}
                            title={action.tooltip}
                        >
                            {action.icon && <i className={action.icon} />}
                            {action.text}
                        </a>
                    );
                })
            }
        </div>
    );
};

const getColumns = (
    userActions?: UserAction[],
    inviteActions?: UserAction[],
    unlockUserCallback?: (user: User) => void)
    : Array<ColumnInfo<User>> => {
    const columns = [
        {
            allowSort: true,
            compareCallback: sortUserDetails,
            grow: true,
            heading: strings.tableHeaderUser,
            renderCallback: renderUserDetails
        },
        {
            allowSort: true,
            compareCallback: sortUserRole,
            heading: strings.tableHeaderRole,
            renderCallback: renderUserRole
        },
        {
            allowSort: true,
            compareCallback: sortLastLogin,
            heading: strings.tableHeaderLastLogin,
            renderCallback: renderLastLogin
        },
        {
            heading: '',
            renderCallback: (user: User) => renderLockedState(user, unlockUserCallback)
        }
    ];

    if (userActions && userActions.length) {
        columns.push({
            heading: '',
            renderCallback: renderUserActions.bind(null, userActions, inviteActions)
        });
    }
    return columns;
};

interface UserTableProps {
    className?: string;    // className to pass through to table component
    users: User[] | null;         // current list of users to display
    userActions?: UserAction[];
    inviteActions?: UserAction[];
    unlockUserCallback?: (user: User) => void;
}

// Wrapper around table component which displays table of user columns:
// user name/email, role, last login date, etc...
const UserTable = (props: UserTableProps): JSX.Element => {
    const { users, className, userActions, inviteActions, unlockUserCallback } = props;

    let classString = 'user-table';
    if (!!className) {
        classString += ` ${className}`;
    }
    return (
        <TableComponent
            data={users || []}
            columns={getColumns(userActions, inviteActions, unlockUserCallback)}
            className={classString}
        />
    );
};

export { UserTable };
export default UserTable;



// WEBPACK FOOTER //
// ./src/components/userTable.tsx